import { useEffect, useState } from "react";
import { DownloadIcon } from "lucide-react";
import { api } from "@/api/client";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Spinner } from "@/components/ui/spinner";

interface BillFileDrawerProps {
  billId: string;
  fileName?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // A bill still in a company inbox is not in the Bills table yet, so its document is signed by a
  // different endpoint under the same id.
  source?: "bill" | "companyInboxBill";
}

export function BillFileDrawer({
  billId,
  fileName,
  open,
  onOpenChange,
  source = "bill",
}: BillFileDrawerProps) {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch a fresh signed URL each time the drawer opens — signed URLs are short-lived.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    async function fetchFileUrl() {
      setLoading(true);
      setError(null);
      setUrl(null);
      const { data, error: apiError } =
        source === "companyInboxBill"
          ? await api.GET("/admin/company-inbox-bills/{billId}/file-url", {
              params: { path: { billId } },
            })
          : await api.GET("/admin/bills/{id}/file-url", {
              params: { path: { id: billId } },
            });
      if (cancelled) return;
      if (apiError || !data?.url) {
        setError("No file is available for this bill.");
      } else {
        setUrl(data.url);
      }
      setLoading(false);
    }

    fetchFileUrl();
    return () => {
      cancelled = true;
    };
  }, [open, billId, source]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full gap-0 p-0 sm:max-w-none sm:w-[640px] lg:w-[820px]"
      >
        <SheetHeader className="flex-row items-center justify-between border-b pr-14">
          <div className="min-w-0">
            <SheetTitle>Bill document</SheetTitle>
            <SheetDescription className="truncate">{fileName ?? "Uploaded file"}</SheetDescription>
          </div>
          <Button asChild size="sm" variant="outline" disabled={!url} className="shrink-0">
            <a
              href={url ?? undefined}
              target="_blank"
              rel="noreferrer noopener"
              download={fileName ?? true}
              aria-disabled={!url}
              onClick={(e) => {
                if (!url) e.preventDefault();
              }}
            >
              <DownloadIcon className="size-4" />
              Download
            </a>
          </Button>
        </SheetHeader>

        <div className="flex-1 overflow-hidden bg-muted">
          {loading ? (
            <div className="flex h-full items-center justify-center">
              <Spinner className="size-5" />
            </div>
          ) : error ? (
            <div className="flex h-full items-center justify-center p-8">
              <p className="text-sm text-muted-foreground">{error}</p>
            </div>
          ) : url ? (
            <iframe src={url} title="Bill document" className="h-full w-full border-0" />
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}
